import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Divider,
  Chip,
  Stack,
  Paper,
  useTheme,
  alpha,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle
} from '@mui/material';
import {
  CalendarToday,
  AccessTime,
  LocationOn,
  CheckCircle,
  PhotoLibrary,
  Close
} from '@mui/icons-material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { keyframes} from '@emotion/react';
import goalogo from 'assets/images/pictures/goa-logo.png';

export default function Wimssday() {
  const theme = useTheme();
  const [selectedImage, setSelectedImage] = React.useState(null);

  // Generate image paths
  const images = Array.from({ length: 10 }, (_, i) => ({
    id: i + 1,
    path: `/wimssday/wimssday_${String(i + 1).padStart(2, '0')}.jpg`,
    alt: `WIMSS Day 2026 - Photo ${i + 1}`
  }));

  const blink=keyframes`
    0% { opacity: 1;}
    50% { opacity: 0;}
    100% (opacity: 1;})  
  `;

  const handleImageClick = (imagePath) => {
    setSelectedImage(imagePath);
  };

  const handleCloseDialog = () => {
    setSelectedImage(null);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        py: { xs: 4, md: 6 },
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
      }}
    >
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Chip
            label="EVENT COMPLETED ✓"
            color="success"
            sx={{ 
              fontWeight: 600, 
              mb: 2,
              bgcolor: alpha(theme.palette.success.main, 0.1),
              color: theme.palette.success.dark
            }}
          />
          <Typography 
            variant="h2" 
            component="h1"
            sx={{ 
              fontWeight: 700, 
              color: theme.palette.primary.dark,
              mb: 1,
              fontSize: { xs: '1.75rem', md: '2.5rem' }
            }}
          >
            WIMSS Day 2026
          </Typography>
          <Typography 
            variant="h5" 
            color="primary"
            sx={{ 
              fontWeight: 500, 
              mb: 3,
              fontStyle: 'italic'
            }}
          >
            联结、健身、复原的一天
          </Typography>
          
          {/* Event Details Bar */}
          <Paper 
            elevation={0}
            sx={{ 
              p: 2, 
              bgcolor: 'white', 
              borderRadius: 2,
              display: 'inline-flex',
              flexWrap: 'wrap',
              gap: 2,
              justifyContent: 'center',
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <CalendarToday color="primary" fontSize="small" />
              <Typography variant="body2" fontWeight={500}>September 5, 2026</Typography>
            </Stack>
            <Divider orientation="vertical" flexItem />
            <Stack direction="row" spacing={1} alignItems="center">
              <AccessTime color="primary" fontSize="small" />
              <Typography variant="body2" fontWeight={500}>9 AM - 3 PM</Typography>
            </Stack>
            <Divider orientation="vertical" flexItem />
            <Stack direction="row" spacing={1} alignItems="center">
              <LocationOn color="primary" fontSize="small" />
              <Typography variant="body2" fontWeight={500}>Site #4, Hawrelak Park, Edmonton</Typography>
            </Stack>
          </Paper>
        </Box>

        {/* Success Banner */}
        <Paper
          elevation={0}
          sx={{
            mb: 6,
            p: 3,
            bgcolor: alpha(theme.palette.success.main, 0.08),
            borderRadius: 2,
            border: `1px dashed ${theme.palette.success.main}`,
            textAlign: 'center'
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
            <Box component="img" src={goalogo} alt="GoA Logo" sx={{ width: 80, height: 'auto'}}/>
            <Typography variant="h6" fontWeight={600} sx={{ animation: `${blink} 1s infinite`, color: "primary.main"}}>
            衷心感谢阿尔伯塔省政府慷慨的资金支持与大力协助，让本次活动得以顺利举办。
            </Typography>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            mb: 6,
            p: 3,
            bgcolor: alpha(theme.palette.success.main, 0.08),
            borderRadius: 2,
            border: `1px dashed ${theme.palette.success.main}`,
            textAlign: 'center'
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
            <CheckCircle color="success" sx={{ fontSize: 32 }} />
            <Typography variant="h6" fontWeight={600} color="success.dark">
            感谢所有150多位参与者、2位徒步教练以及40多位志愿者！
            </Typography>
          </Stack>
        </Paper>

        {/* Article Section */}
        <Card 
          elevation={0} 
          sx={{ 
            mb: 6, 
            bgcolor: 'white',
            border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
            borderRadius: 3,
            overflow: 'hidden'
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Typography 
              variant="h4" 
              fontWeight={700} 
              gutterBottom 
              color="primary.dark"
              sx={{ mb: 3, lineHeight: 1.3 }}
            >
              WIMSS Day 2026徒步活动圆满成功：社区齐聚河谷，共促健康与环保
            </Typography>
            
            <Typography 
              variant="subtitle1" 
              color="text.secondary" 
              sx={{ mb: 3, fontStyle: 'italic' }}
            >
              埃德蒙顿，阿尔伯塔省 — 2026年9月8日 
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mb: 3, textAlign: 'left'}}>
              2026年9月5日，正如WIMSS Day 2026 Hiking Event Plan所规划的那样，备受瞩目的WIMSS徒步活动在Hawrelak公园成功举办，将这里变成了一个充满活力的跨文化交流与户外运动中心。当天的天气堪称完美：多云无雨，气温维持在凉爽宜人的15摄氏度左右。150多位徒步爱好者齐聚风景如画的北萨斯喀彻温河谷，共同践行“联结、健身、复原”的活动主题。
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mb: 3, textAlign: 'left'}}>
              此次活动完美实现了建立凝聚力社区和推广健康生活方式的核心使命。参与者们热情高涨地踏上步道，不仅结识了许多新朋友、建立了深厚的友谊，还身体力行地参与了沿途的垃圾清理工作，用实际行动保护了当地的生态环境。除了原定计划的团队徒步和趣味竞赛外，充满活力的参与者们还自发组织了打排球和在树林中采蘑菇等丰富多彩的娱乐活动，为整个公园注入了无限生机。 
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mb: 3, textAlign: 'left'}}>
              在上午的徒步结束后，大家齐聚一堂共享披萨，在欢声笑语中进一步加深了彼此的跨文化友谊。在两名专业徒步教练和40多名热心志愿者的全力支持与保驾护航下，本次活动圆满实现了所有的策划目标。从庆祝阿尔伯塔省的自然风光，到促进大众的身心健康，今年的WIMSS Day无疑是一场里程碑式的成功盛会，为埃德蒙顿的多元文化户外休闲社区留下了深远而积极的影响。             </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mb: 3, textAlign: 'left'}}>
              我们的一位徒步活动参与者在活动结束后给我们发来了非常棒的反馈：
            </Typography>

            <Typography variant="body1" component="blockquote" color="text.secondary" sx={{ lineHeight: 1.8, textAlign: 'left', fontStyle: 'italic'}}>
            “这是一次令人难忘的美好体验！WIMSS Day 2026 徒步活动让我们有机会欣赏美丽的河谷风光、锻炼身体，同时也认识和结交了许多优秀的朋友。当天的天气稍微有些凉爽，阳光也没有出来，但这反而让整个徒步过程更加舒适、清爽。整个活动组织得非常周到，徒步教练们友善、热情，一整天都给予大家鼓励和支持。我非常喜欢活动中充满正能量的氛围，以及大家热情友好的互动。衷心感谢 WIMSS 团队、所有志愿者、徒步教练和赞助商，是你们的支持让这次活动成为一段如此难忘的经历！期待下一次再见！”
            </Typography>
          </CardContent>
        </Card>

        {/* Photo Gallery Section */}
        <Typography 
          variant="h4" 
          fontWeight={700} 
          align="center" 
          gutterBottom 
          sx={{ mb: 1, color: theme.palette.primary.dark }}
        >
          活动照片集
        </Typography>
        <Typography 
          variant="body2" 
          color="text.secondary" 
          align="center" 
          sx={{ mb: 4 }}
        >
          点击任何照片查看全尺寸
        </Typography>

        <Grid container spacing={2} sx={{ mb: 6 }}>
          {images.map((image) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={image.id}>
              <Card 
                elevation={0}
                sx={{
                  height: 280,
                  overflow: 'hidden',
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8]
                  }
                }}
                onClick={() => handleImageClick(image.path)}
              >
                <Box
                  component="img"
                  src={image.path}
                  alt={image.alt}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block'
                  }}
                />
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Footer */}
        <Divider sx={{ my: 4 }} />
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            🌿 WIMSS Day 2026 • Alberta Community Hiking Festival • Hawrelak Park, Edmonton
          </Typography>
          {/* <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
            © 2026 WIMSS. All rights reserved.
          </Typography> */}
        </Box>
      </Container>

      {/* Full Size Image Dialog */}
      <Dialog
        open={!!selectedImage}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            bgcolor: 'rgba(0, 0, 0, 0.9)',
            maxWidth: '95vw'
          }
        }}
      >
        <DialogTitle sx={{ color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          全尺寸照片
          <IconButton 
            onClick={handleCloseDialog} 
            size="small"
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, textAlign: 'center' }}>
          {selectedImage && (
            <Box
              component="img"
              src={selectedImage}
              alt="Full size"
              sx={{
                width: '100%',
                maxHeight: '85vh',
                objectFit: 'contain',
                display: 'block'
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}